import { Suspense } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { RouteTitleSync } from "./components/RouteTitleSync";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { Layout } from "./components/Layout";
import { StepperProvider } from "./contexts/StepperContext";
import { ConfigurationProvider } from "./contexts/ConfigurationContext";
import { ScientificLoader } from "@/components/ScientificLoader";
import { lazyWithReload, installChunkLoadErrorHandler } from "@/lib/lazy-with-reload";

// Once-per-process: catch dynamic-import failures that didn't go
// through a lazy() boundary (e.g. router preloads, Radix dropdown
// content imports) and reload to a fresh index.html. Stale tabs after
// a deploy were surfacing as "Failed to fetch dynamically imported
// module" with no recovery before this.
installChunkLoadErrorHandler();

// All lazy routes are wrapped so a stale chunk hash auto-recovers via
// reload. Behaviour is identical to React.lazy on the happy path.
const lazy = lazyWithReload;

// ── Eagerly-loaded routes ─────────────────────────────────────────────
// Login / Dashboard / NotFound are the entry points users hit on first
// paint; loading them lazily would cost an extra round-trip before they
// see anything. Everything else is split into its own chunk below so the
// initial bundle stays small instead of bundling every page upfront.
import Dashboard from "./pages/Dashboard";
import Login from "./pages/Login";
import NotFound from "./pages/NotFound";

// ── Lazy routes (each is its own chunk) ───────────────────────────────
// Auth follow-up pages and the SynqAI landing are lazy too: they pulled
// the animated supply-chain scene and recharts (via RichMessageRenderer)
// into the main chunk for every visitor. Login stays eager for first paint.
const Register = lazy(() => import("./pages/Register"));
const SignupSuccess = lazy(() => import("./pages/SignupSuccess"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const SynqAIPage = lazy(() => import("./pages/SynqAIPage"));
const Analytics = lazy(() => import("./pages/Analytics"));
const DemandForecasting = lazy(() => import("./pages/DemandForecasting"));
const InventoryOptimization = lazy(() => import("./pages/InventoryOptimization"));
const ReplenishmentPlanning = lazy(() => import("./pages/ReplenishmentPlanning"));
const AssortmentPlanning = lazy(() => import("./pages/AssortmentPlanning"));
const OpexPlanning = lazy(() => import("./pages/OpexPlanning"));
const Foundry = lazy(() => import("./pages/Foundry"));
const Jobs = lazy(() => import("./pages/Jobs"));
const AddLookupPage = lazy(() => import("./pages/AddLookupPage"));
const EntityPreview = lazy(() => import("./pages/EntityPreview"));
const ProcurementPlanning = lazy(() => import("./pages/ProcurementPlanning"));
const Workflows = lazy(() => import("./pages/Workflows"));
const WorkflowBuilder = lazy(() => import("./pages/WorkflowBuilder"));
const WorkflowMonitor = lazy(() => import("./pages/WorkflowMonitor"));
const WorkflowRun = lazy(() => import("./pages/WorkflowRun"));
const PipelineDashboard = lazy(() => import("./pages/PipelineDashboard"));
const Build = lazy(() => import("./pages/Build"));
const UserManagement = lazy(() => import("./pages/UserManagement"));
const Settings = lazy(() => import("./pages/Settings"));
const Profile = lazy(() => import("./pages/Profile"));
const Billing = lazy(() => import("./pages/Billing"));
const TSPResultsPage = lazy(() => import("./pages/TSPResultsPage"));
const ProductionPlanning = lazy(() => import("./pages/ProductionPlanning"));
const RawMaterialPlanning = lazy(() => import("./pages/RawMaterialPlanning"));
const FirstMidMileOptimization = lazy(() => import("./pages/FirstMidMileOptimization"));
const CapexPlanning = lazy(() => import("./pages/CapexPlanning"));
const ProjectPrioritization = lazy(() => import("./pages/ProjectPrioritization"));
const Reports = lazy(() => import("./pages/Reports"));
const TargetSalesPlanning = lazy(() => import("./pages/TargetSalesPlanning"));
const AdminStudies = lazy(() => import("./pages/AdminStudies"));
const AdminAuditLog = lazy(() => import("./pages/AdminAuditLog"));
const RoleManagement = lazy(() => import("./pages/admin/RoleManagement"));
const UserInvitations = lazy(() => import("./pages/admin/UserInvitations"));
const AcceptInvitation = lazy(() => import("./pages/AcceptInvitation"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));

const queryClient = new QueryClient();

// Tiny loader for code-split chunks. Reuses the existing ScientificLoader
// so the look matches every other busy state in the app.
const RouteFallback = () => <ScientificLoader />;

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ConfigurationProvider>
    <StepperProvider>
      <TooltipProvider>
        {/* Single notification system: the legacy useToast() hook now
            forwards to sonner, so one <Toaster/> renders everything. */}
        <Toaster richColors closeButton position="bottom-right" />
        <BrowserRouter>
          <RouteTitleSync />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              {/* Post-signup confirmation screen + the welcome-email
                  verify landing. Both are public; the verify endpoint
                  itself takes care of token validation. */}
              <Route path="/signup-success" element={<SignupSuccess />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              {/* Public invitation accept landing — must live outside
                  ProtectedRoute because the invitee likely doesn't have
                  an active session yet. */}
              <Route path="/accept-invitation" element={<AcceptInvitation />} />
              <Route path="/" element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route index element={<SynqAIPage />} />
                <Route path="study" element={<Dashboard />} />

                <Route path="foundry" element={<Foundry />} />
                <Route path="jobs" element={<Jobs />} />
                <Route path="/data-jobs" element={<Jobs />} />
                <Route path="/add-lookup" element={<AddLookupPage />} />
                <Route path="/entity-preview/:entityName" element={<EntityPreview />} />
                <Route path="demand-forecasting" element={<DemandForecasting />} />
                <Route path="assortment-planning" element={<AssortmentPlanning />} />
                <Route path="inventory-optimization" element={<InventoryOptimization />} />
                <Route path="replenishment-planning" element={<ReplenishmentPlanning />} />
                <Route path="production-planning" element={<ProductionPlanning />} />
                <Route path="raw-material-planning" element={<RawMaterialPlanning />} />
                <Route path="logistics-optimization" element={<FirstMidMileOptimization />} />
                <Route path="opex-planning" element={<OpexPlanning />} />
                <Route path="capex-planning" element={<CapexPlanning />} />
                <Route path="project-prioritization" element={<ProjectPrioritization />} />
                {/* /detailed-production-scheduling removed — folded into Production Planning & Scheduling */}
                <Route path="procurement-planning" element={<ProcurementPlanning />} />
                <Route path="target-sales-planning" element={<TargetSalesPlanning />} />
                <Route path="tsp-results" element={<TSPResultsPage />} />
                <Route path="admin/studies" element={<AdminStudies />} />
                <Route path="admin/audit-log" element={<AdminAuditLog />} />
                <Route path="admin/roles" element={<RoleManagement />} />
                <Route path="admin/invitations" element={<UserInvitations />} />
                {/* Platform admin (SuperAdmin only). The page itself
                    soft-renders an access-denied card for non-superusers;
                    the BE is the actual gate. */}
                <Route path="admin/platform" element={<AdminPanel />} />
                <Route path="workflows" element={<Workflows />} />
                <Route path="workflow-builder" element={<WorkflowBuilder />} />
                <Route path="workflow-monitor" element={<WorkflowMonitor />} />
                <Route path="workflow-run" element={<WorkflowRun />} />
                <Route path="pipeline-dashboard" element={<PipelineDashboard />} />
                <Route path="reports" element={<Reports />} />
                <Route path="build" element={<Build />} />
                <Route path="users" element={<UserManagement />} />
                <Route path="settings" element={<Settings />} />
                <Route path="profile" element={<Profile />} />
                <Route path="billing" element={<Billing />} />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </StepperProvider>
    </ConfigurationProvider>
  </QueryClientProvider>
);

export default App;
